import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { 
  Wrench, Shield, Check, ChevronRight, ChevronLeft,
  Award, FileCheck, Loader2, X, ArrowLeft, User, 
  ChevronDown, LogOut
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// ========== ONBOARDING STEP DEFINITIONS ==========
const ONBOARDING_STEPS = [
  { id: 1, name: 'Trades & Experience', description: 'Select your trades' },
  { id: 2, name: 'Certifications', description: 'Declare your qualifications' },
  { id: 3, name: 'Review & Complete', description: 'Confirm your profile' }
];

const ContractorOnboarding = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  // Core state
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [certificationTypes, setCertificationTypes] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [exitAction, setExitAction] = useState(null);
  const [existingProfile, setExistingProfile] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    company_name: '',
    skills: [],
    years_experience: 0,
    service_radius_miles: 15,
    declared_certifications: []
  });

  const availableSkills = [
    { id: 'Plumbing', name: 'Plumbing', icon: '🔧' },
    { id: 'Electrical', name: 'Electrical', icon: '⚡' },
    { id: 'Heating', name: 'Heating', icon: '🔥' },
    { id: 'Handyman', name: 'Handyman', icon: '🛠️' },
    { id: 'Carpentry', name: 'Carpentry', icon: '🪚' },
    { id: 'Roofing', name: 'Roofing', icon: '🏠' },
    { id: 'Appliances', name: 'Appliances', icon: '🔌' },
    { id: 'Pest Control', name: 'Pest Control', icon: '🐛' }
  ];

  // ========== NAVIGATION HELPERS ==========
  
  // Get return URL from session storage or default to dashboard
  const getReturnUrl = () => {
    const returnTo = sessionStorage.getItem('contractorOnboardingReturnTo');
    return returnTo || '/dashboard';
  };

  // Store return URL when entering onboarding
  useEffect(() => {
    const currentReturnTo = sessionStorage.getItem('contractorOnboardingReturnTo');
    if (!currentReturnTo && user) {
      // Store where user came from (default to customer dashboard)
      sessionStorage.setItem('contractorOnboardingReturnTo', '/dashboard');
    }
  }, [user]);

  // ========== DATA LOADING ==========
  
  useEffect(() => {
    const initializeOnboarding = async () => {
      setInitialLoading(true);
      await Promise.all([
        fetchCertificationTypes(),
        fetchExistingDraft()
      ]);
      setInitialLoading(false);
    };
    
    initializeOnboarding();
  }, []);

  const fetchCertificationTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/certifications/types`);
      setCertificationTypes(response.data.certification_types || []);
    } catch (error) {
      console.error('Error fetching certification types:', error);
    }
  };

  const fetchExistingDraft = async () => {
    try {
      const response = await axios.get(`${API_URL}/contractor-onboarding/status`);
      const data = response.data;
      
      if (data.onboarding_status !== 'none' && data.id) {
        setExistingProfile(data);
        
        // Resume from last step
        const resumeStep = data.onboarding_step || 1;
        setStep(resumeStep);
        
        // Populate form data
        setFormData({
          company_name: data.company_name || '',
          skills: data.skills || [],
          years_experience: data.years_experience || 0,
          service_radius_miles: data.service_radius_miles || 15,
          declared_certifications: data.declared_certifications || []
        });
        
        // Handle different statuses
        if (data.onboarding_status === 'submitted') {
          toast.info('Your profile is under review. You can view but not edit.');
        } else if (data.onboarding_status === 'rejected') {
          toast.warning('Your profile needs updates. Please review and resubmit.');
        }
      }
    } catch (error) {
      // No existing draft - start fresh
      console.log('No existing draft found, starting fresh');
    }
  };

  // ========== DRAFT SAVING ==========
  
  const saveDraft = useCallback(async (showToast = true) => {
    if (savingDraft) return;
    
    setSavingDraft(true);
    try {
      await axios.post(`${API_URL}/contractor-onboarding/save-draft`, {
        company_name: formData.company_name,
        skills: formData.skills,
        years_experience: formData.years_experience,
        service_radius_miles: formData.service_radius_miles,
        declared_certifications: formData.declared_certifications,
        onboarding_step: step
      });
      
      setHasUnsavedChanges(false);
      if (showToast) {
        toast.success('Progress saved');
      }
    } catch (error) {
      console.error('Failed to save draft:', error);
      if (showToast) {
        toast.error('Failed to save progress');
      }
    } finally {
      setSavingDraft(false);
    }
  }, [formData, step, savingDraft]);

  // Auto-save on step change
  useEffect(() => {
    if (!initialLoading && hasUnsavedChanges) {
      const timer = setTimeout(() => {
        saveDraft(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [formData, initialLoading, hasUnsavedChanges, saveDraft]);

  // ========== FORM HANDLERS ==========
  
  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
    setHasUnsavedChanges(true);
  };

  const toggleSkill = (skillId) => {
    const newSkills = formData.skills.includes(skillId)
      ? formData.skills.filter(s => s !== skillId)
      : [...formData.skills, skillId];
    updateFormData({ skills: newSkills });
  };

  const toggleCertification = (certId) => {
    const newCerts = formData.declared_certifications.includes(certId)
      ? formData.declared_certifications.filter(c => c !== certId)
      : [...formData.declared_certifications, certId];
    updateFormData({ declared_certifications: newCerts });
  };

  // ========== NAVIGATION ACTIONS ==========
  
  const goNext = async () => {
    if (step === 1 && formData.skills.length === 0) {
      toast.error('Please select at least one trade');
      return;
    }
    
    // Save before moving to next step
    await saveDraft(false);
    setStep(prev => Math.min(prev + 1, 3));
  };

  const goBack = async () => {
    if (step === 1) {
      // On step 1, back returns to customer area
      handleExitRequest('back');
    } else {
      await saveDraft(false);
      setStep(prev => Math.max(prev - 1, 1));
    }
  };

  const handleExitRequest = (action) => {
    if (hasUnsavedChanges) {
      setExitAction(action);
      setShowExitDialog(true);
    } else {
      performExit();
    }
  };

  const performExit = async (saveFirst = false) => {
    if (saveFirst) {
      await saveDraft(true);
    }
    
    // Clear return URL
    sessionStorage.removeItem('contractorOnboardingReturnTo');
    
    // Navigate back to customer area
    navigate(getReturnUrl());
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      toast.error('Failed to log out');
    }
  };

  // ========== SUBMIT HANDLER ==========
  
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.skills.length === 0) {
      toast.error('Please select at least one trade');
      return;
    }

    setLoading(true);
    try {
      // First save the final draft
      await saveDraft(false);
      
      // Then submit for review
      await axios.post(`${API_URL}/contractor-onboarding/submit`);
      
      // Clear return URL
      sessionStorage.removeItem('contractorOnboardingReturnTo');

      toast.success('Profile submitted! You can now access contractor features.');
      navigate('/contractor/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit profile');
    } finally {
      setLoading(false);
    }
  };

  // ========== COMPUTED VALUES ==========
  
  const isAuthenticated = !!user;
  const isProfileEditable = !existingProfile || 
    existingProfile.onboarding_status === 'draft' || 
    existingProfile.onboarding_status === 'rejected';

  // Group certifications by category
  const groupedCertifications = certificationTypes.reduce((acc, cert) => {
    const category = cert.category || 'general';
    if (!acc[category]) acc[category] = [];
    acc[category].push(cert);
    return acc;
  }, {});

  const categoryLabels = {
    insurance: 'Insurance & Liability',
    identity: 'Business Registration',
    gas: 'Gas Certifications',
    electrical: 'Electrical Certifications',
    safety: 'Safety & Compliance',
    general: 'Trade Qualifications'
  };

  // ========== LOADING STATE ==========
  
  if (initialLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-zinc-400" />
          <p className="text-zinc-500">Loading your profile...</p>
        </div>
      </div>
    );
  }

  // ========== RENDER ==========
  
  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Exit Confirmation Dialog */}
      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save your progress?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Would you like to save your progress before leaving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={() => setShowExitDialog(false)}>
              Cancel
            </AlertDialogCancel>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowExitDialog(false);
                performExit(false);
              }}
            >
              Exit without saving
            </Button>
            <AlertDialogAction 
              onClick={() => {
                setShowExitDialog(false);
                performExit(true);
              }}
              className="bg-zinc-900 hover:bg-zinc-800"
            >
              Save & Exit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Navigation Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-neutral-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Left side - Logo + Back/Exit controls */}
            <div className="flex items-center gap-4">
              <Link to="/" className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                ZOLT
              </Link>
              
              {/* Back/Exit controls - visible for authenticated users */}
              {isAuthenticated && (
                <div className="hidden sm:flex items-center gap-2 ml-4 pl-4 border-l border-neutral-200">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={goBack}
                    className="text-neutral-600 hover:text-black"
                    data-testid="onboarding-back-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    {step === 1 ? 'Exit Setup' : 'Back'}
                  </Button>
                  
                  {step > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleExitRequest('exit')}
                      className="text-neutral-500 hover:text-neutral-700"
                      data-testid="onboarding-exit-btn"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Exit Setup
                    </Button>
                  )}
                </div>
              )}
            </div>
            
            {/* Center - Step indicator (mobile) */}
            <div className="sm:hidden text-sm text-neutral-500">
              Step {step} of 3
            </div>
            
            {/* Right side - Auth-dependent navigation */}
            <div className="flex items-center gap-4">
              {isAuthenticated ? (
                // Authenticated: Show user menu
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="flex items-center gap-2 px-3 py-2"
                      data-testid="user-menu-trigger"
                    >
                      <div className="w-8 h-8 bg-zinc-200 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-zinc-600" />
                      </div>
                      <span className="hidden sm:inline text-sm font-medium text-zinc-700">
                        {user?.name?.split(' ')[0]}
                      </span>
                      <ChevronDown className="w-4 h-4 text-zinc-500" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>
                      {user?.name}
                      <p className="text-xs text-zinc-500 font-normal">{user?.email}</p>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    
                    <DropdownMenuItem
                      onClick={() => handleExitRequest('dashboard')}
                      className="cursor-pointer"
                      data-testid="menu-return-customer"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Return to Dashboard
                    </DropdownMenuItem>
                    
                    <DropdownMenuSeparator />
                    
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="text-red-600 cursor-pointer focus:text-red-600 focus:bg-red-50"
                      data-testid="menu-logout"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                // Not authenticated: Show login/signup
                <>
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/auth?mode=login')}
                    className="text-neutral-700 hidden sm:flex"
                  >
                    Log in
                  </Button>
                  <Button 
                    onClick={() => navigate('/auth?mode=signup')}
                    className="bg-black hover:bg-neutral-800 text-white rounded-full px-6"
                  >
                    Sign up
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Mobile Back/Exit bar */}
        {isAuthenticated && (
          <div className="sm:hidden border-t border-neutral-100 px-4 py-2 flex items-center justify-between bg-neutral-50">
            <Button
              variant="ghost"
              size="sm"
              onClick={goBack}
              className="text-neutral-600"
              data-testid="onboarding-back-btn-mobile"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              {step === 1 ? 'Exit' : 'Back'}
            </Button>
            
            {savingDraft && (
              <span className="text-xs text-neutral-400 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Saving...
              </span>
            )}
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleExitRequest('exit')}
              className="text-neutral-500"
              data-testid="onboarding-exit-btn-mobile"
            >
              <X className="w-4 h-4 mr-1" />
              Exit
            </Button>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className={`flex-1 pb-16 ${isAuthenticated ? 'pt-28 sm:pt-24' : 'pt-24'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-2 mb-12">
            {ONBOARDING_STEPS.map((s) => (
              <div key={s.id} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                  step >= s.id ? 'bg-black text-white' : 'bg-neutral-100 text-neutral-400'
                }`}>
                  {step > s.id ? <Check className="w-5 h-5" /> : s.id}
                </div>
                {s.id < 3 && <div className={`w-16 h-0.5 mx-2 ${step > s.id ? 'bg-black' : 'bg-neutral-200'}`} />}
              </div>
            ))}
          </div>

          {/* Read-only mode banner */}
          {existingProfile?.onboarding_status === 'submitted' && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-center gap-3">
              <Shield className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-blue-900">Profile Under Review</p>
                <p className="text-sm text-blue-700">Your profile has been submitted and is being reviewed. You cannot make changes during this time.</p>
              </div>
            </div>
          )}

          {existingProfile?.onboarding_status === 'rejected' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-center gap-3">
              <Shield className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-900">Updates Required</p>
                <p className="text-sm text-amber-700">
                  {existingProfile.rejection_reason || 'Please review your profile and make the necessary updates before resubmitting.'}
                </p>
              </div>
            </div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-2xl shadow-black/5 border border-neutral-100 p-8"
          >
            {/* Step 1: Trades & Experience */}
            {step === 1 && (
              <div className="space-y-8">
                <div className="text-center">
                  <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    What services do you offer?
                  </h1>
                  <p className="text-neutral-500">Select your trades and tell us about your experience</p>
                </div>

                <div>
                  <Label className="text-sm font-medium mb-3 block">Company Name (Optional)</Label>
                  <Input
                    data-testid="company-name-input"
                    type="text"
                    placeholder="e.g., Smith Plumbing Ltd"
                    value={formData.company_name}
                    onChange={(e) => updateFormData({ company_name: e.target.value })}
                    disabled={!isProfileEditable}
                    className="h-12"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium mb-3 block">Your Trades *</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {availableSkills.map((skill) => (
                      <button
                        key={skill.id}
                        type="button"
                        onClick={() => isProfileEditable && toggleSkill(skill.id)}
                        disabled={!isProfileEditable}
                        className={`p-4 rounded-xl border-2 text-center transition-all ${
                          formData.skills.includes(skill.id)
                            ? 'border-black bg-black text-white'
                            : 'border-neutral-200 hover:border-neutral-400'
                        } ${!isProfileEditable ? 'opacity-60 cursor-not-allowed' : ''}`}
                        data-testid={`skill-${skill.id.toLowerCase()}`}
                      >
                        <div className="text-2xl mb-1">{skill.icon}</div>
                        <div className="font-medium text-sm">{skill.name}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm font-medium mb-3 block">Years of Experience</Label>
                    <Input
                      data-testid="experience-input"
                      type="number"
                      min="0"
                      value={formData.years_experience}
                      onChange={(e) => updateFormData({ years_experience: parseInt(e.target.value) || 0 })}
                      disabled={!isProfileEditable}
                      className="h-12"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-3 block">Service Radius (miles)</Label>
                    <Input
                      data-testid="radius-input"
                      type="number"
                      min="1"
                      max="30"
                      value={formData.service_radius_miles}
                      onChange={(e) => updateFormData({ service_radius_miles: parseInt(e.target.value) || 10 })}
                      disabled={!isProfileEditable}
                      className="h-12"
                    />
                    <p className="text-xs text-neutral-500 mt-1">Maximum 30 miles</p>
                  </div>
                </div>

                <Button
                  onClick={goNext}
                  disabled={formData.skills.length === 0}
                  className="w-full h-14 bg-black hover:bg-neutral-800 text-white rounded-xl text-lg"
                  data-testid="step1-next"
                >
                  Continue <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}

            {/* Step 2: Certifications & Compliance */}
            {step === 2 && (
              <div className="space-y-8">
                <div className="text-center">
                  <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Certifications & Compliance
                  </h1>
                  <p className="text-neutral-500">Select the certifications and registrations you hold</p>
                </div>

                {/* Info Banner */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3">
                  <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <strong>You can start onboarding now.</strong> Verification is required to display trust badges and receive certain job types. You can upload documents later from your profile.
                  </div>
                </div>

                <div className="space-y-6 max-h-[400px] overflow-y-auto pr-2">
                  {Object.entries(groupedCertifications).map(([category, certs]) => (
                    <div key={category}>
                      <h3 className="font-semibold text-neutral-700 mb-3 flex items-center gap-2">
                        {category === 'insurance' && <FileCheck className="w-4 h-4" />}
                        {category === 'gas' && <span>🔥</span>}
                        {category === 'electrical' && <span>⚡</span>}
                        {category === 'safety' && <Shield className="w-4 h-4" />}
                        {categoryLabels[category] || category}
                      </h3>
                      <div className="space-y-2">
                        {certs.map((cert) => (
                          <label
                            key={cert.id}
                            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                              formData.declared_certifications.includes(cert.id)
                                ? 'border-black bg-neutral-50'
                                : 'border-neutral-200 hover:border-neutral-300'
                            } ${!isProfileEditable ? 'opacity-60 cursor-not-allowed' : ''}`}
                          >
                            <Checkbox
                              checked={formData.declared_certifications.includes(cert.id)}
                              onCheckedChange={() => isProfileEditable && toggleCertification(cert.id)}
                              disabled={!isProfileEditable}
                              className="mt-0.5"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-sm">{cert.name}</div>
                              {cert.required_for_trades && cert.required_for_trades.length > 0 && (
                                <div className="text-xs text-neutral-500 mt-0.5">
                                  Required for: {cert.required_for_trades.join(', ')}
                                </div>
                              )}
                            </div>
                            {cert.requires_document && (
                              <span className="text-xs text-neutral-400">Doc required</span>
                            )}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={goBack}
                    className="flex-1 h-14 rounded-xl"
                    data-testid="step2-back"
                  >
                    <ChevronLeft className="w-5 h-5 mr-2" /> Back
                  </Button>
                  <Button
                    onClick={goNext}
                    className="flex-1 h-14 bg-black hover:bg-neutral-800 text-white rounded-xl"
                    data-testid="step2-next"
                  >
                    Continue <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Review & Complete */}
            {step === 3 && (
              <div className="space-y-8">
                <div className="text-center">
                  <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Review & Complete
                  </h1>
                  <p className="text-neutral-500">Make sure everything looks correct</p>
                </div>

                <div className="space-y-4">
                  {/* Trades */}
                  <div className="bg-neutral-50 rounded-xl p-4">
                    <div className="flex justify-between items-start mb-2">
                      <Label className="text-sm text-neutral-500">Your Trades</Label>
                      {isProfileEditable && (
                        <button onClick={() => setStep(1)} className="text-sm text-blue-600 hover:underline">Edit</button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.skills.map((skill) => (
                        <span key={skill} className="px-3 py-1 bg-black text-white text-sm rounded-full">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Experience & Radius */}
                  <div className="bg-neutral-50 rounded-xl p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-neutral-500">Experience</Label>
                        <p className="font-medium">{formData.years_experience} years</p>
                      </div>
                      <div>
                        <Label className="text-sm text-neutral-500">Service Radius</Label>
                        <p className="font-medium">{formData.service_radius_miles} miles</p>
                      </div>
                    </div>
                  </div>

                  {/* Certifications */}
                  <div className="bg-neutral-50 rounded-xl p-4">
                    <div className="flex justify-between items-start mb-2">
                      <Label className="text-sm text-neutral-500">Declared Certifications</Label>
                      {isProfileEditable && (
                        <button onClick={() => setStep(2)} className="text-sm text-blue-600 hover:underline">Edit</button>
                      )}
                    </div>
                    {formData.declared_certifications.length > 0 ? (
                      <div className="space-y-1">
                        {formData.declared_certifications.map((certId) => {
                          const cert = certificationTypes.find(c => c.id === certId);
                          return cert ? (
                            <div key={certId} className="flex items-center gap-2 text-sm">
                              <Award className="w-4 h-4 text-neutral-400" />
                              <span>{cert.name}</span>
                              <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">Pending verification</span>
                            </div>
                          ) : null;
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-neutral-500">No certifications declared yet</p>
                    )}
                  </div>
                </div>

                {/* Info Banner */}
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-green-800">
                    <strong>Almost there!</strong> After completing your profile, you&apos;ll be able to go online and start receiving job offers in your area.
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={goBack}
                    className="flex-1 h-14 rounded-xl"
                    data-testid="step3-back"
                  >
                    <ChevronLeft className="w-5 h-5 mr-2" /> Back
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={loading || !isProfileEditable}
                    className="flex-1 h-14 bg-black hover:bg-neutral-800 text-white rounded-xl text-lg"
                    data-testid="submit-onboarding-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : existingProfile?.onboarding_status === 'submitted' ? (
                      <>Under Review</>
                    ) : (
                      <>Complete Profile</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 bg-neutral-50 border-t border-neutral-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>ZOLT</div>
            <div className="flex gap-6 text-sm text-neutral-600">
              <Link to="/#how-it-works" className="hover:text-black">How it works</Link>
              <Link to="/#pricing" className="hover:text-black">Pricing</Link>
              <a href="#" className="hover:text-black">Privacy</a>
              <a href="#" className="hover:text-black">Terms</a>
            </div>
            <p className="text-sm text-neutral-500">© 2026 ZOLT X Limited</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContractorOnboarding;
